"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { adaptMenuData, MenuNode } from "../header/v2/menuAdapter";
import { fetchFooterMenu } from "@/api-service/footer";
import { fetchAllPages } from "@/api-service/pages";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { fetchGeneralSettings } from "@/api-service/general";
import type { GeneralSettingsResponse } from "@/packages/types/general";
import type { PagesListApiResponse } from "@/packages/types/page";
import LABELS from "@/components/global-constant/src/general";
import TrustpilotFooterSection from "./TrustpilotFooterSection";
import "swiper/css";

// Fallback hierarchical categories matching DistaCart layout structure
const fallbackCategories: MenuNode[] = [

];

// Interactive Serving Cities Tab Data
const citiesData: Record<string, string> = {
  USA: "New York, Los Angeles, Chicago, Houston, Phoenix, Philadelphia, San Antonio, San Diego, Dallas, San Jose, Austin, Seattle, Denver, Boston, Portland, Miami, Atlanta, Tampa, Orlando, Las Vegas, Sacramento, San Francisco, Nashville, Charlotte, Columbus, Detroit, Cleveland, Indianapolis, Kansas City, Minneapolis, Salt Lake City, Raleigh, Buffalo, Rochester, Irvine, Fremont, Arlington, Garland, Plano, Aurora, St. Louis, Milwaukee, Omaha, Tulsa, Wichita, Madison, Boise, Spokane, Reno, Provo, Thornton, Roseville, Denton, Waco, Surprise, Carrollton, West Valley City, Charleston, Warren, Hampton, Gainesville, Visalia, Coral Springs, Columbia, Cedar Rapids, Stamford, Santa Clara, Round Rock, Thousand Oaks, Hartford, Pearland, Ann Arbor, Richardson, Lansing, Clearwater, Palm Bay, Costa Mesa, Westminster, Lakeland, Billings, Hillsboro",
  Canada: "Toronto, Montreal, Vancouver, Calgary, Edmonton, Ottawa, Winnipeg, Quebec City, Hamilton, Kitchener, London, Victoria, Halifax, Mississauga, Brampton, Surrey, Burnaby, Richmond, Markham, Vaughan, Laval, Longueuil, Kelowna, Barrie, Regina, Saskatoon, Moncton, Fredericton, Saint John, Thunder Bay, Red Deer, Nanaimo",
  Australia: "Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Canberra, Newcastle, Wollongong, Hobart, Darwin, Cairns, Townsville, Geelong, Bendigo, Ballarat, Toowoomba, Sunshine Coast",
  Singapore: "Downtown Core, Jurong East, Tampines, Woodlands, Bedok, Yishun, Ang Mo Kio, Hougang, Sengkang, Punggol, Choa Chu Kang, Bukit Batok, Clementi, Queenstown, Bukit Merah, Geylang, Pasir Ris, Toa Payoh",
  newzealand: "Auckland, Wellington, Christchurch, Hamilton, Tauranga, Dunedin, Palmerston North, Napier, Nelson",
  Malaysia: "Kuala Lumpur, George Town, Johor Bahru, Ipoh, Shah Alam, Kota Kinabalu, Kuching, Malacca, Seremban, Petaling Jaya",
  UAE: "Dubai, Abu Dhabi, Sharjah, Ajman, Ras Al Khaimah, Fujairah, Al Ain",
  SaudiArabia: "Riyadh, Jeddah, Mecca, Medina, Dammam, Khobar, Taif",
  Qatar: "Doha, Al Wakrah, Al Rayyan, Lusail",
  Kuwait: "Kuwait City, Hawalli, Salmiya, Farwaniya",
  Oman: "Muscat, Salalah, Sohar, Nizwa",
  UK: "London, Birmingham, Manchester, Glasgow, Liverpool, Leeds, Sheffield, Bristol, Leicester, Coventry, Nottingham, Newcastle, Southampton, Derby, Portsmouth, Reading, Milton Keynes, Cardiff, Belfast, Edinburgh, York, Oxford, Cambridge, Brighton, Plymouth, Bournemouth, Norwich, Swindon, Luton, Watford, Slough, Blackpool, Sunderland, Wolverhampton",
  Ireland: "Dublin, Cork, Galway, Limerick, Waterford, Drogheda, Dundalk, Bray, Swords, Kilkenny, Athlone, Navan",
  Germany: "Berlin, Hamburg, Munich, Cologne, Frankfurt, Stuttgart, Dusseldorf, Dortmund, Essen, Leipzig, Bremen, Dresden, Hanover, Nuremberg, Bonn, Mannheim, Karlsruhe, Augsburg, Wiesbaden, Munster",
  France: "Paris, Marseille, Lyon, Toulouse, Nice, Nantes, Strasbourg, Montpellier, Bordeaux, Lille, Rennes, Reims, Toulon, Grenoble, Dijon, Angers, Brest, Clermont-Ferrand",
  Italy: "Rome, Milan, Naples, Turin, Palermo, Genoa, Bologna, Florence, Bari, Catania, Venice, Verona, Padua, Trieste, Parma, Modena",
  Spain: "Madrid, Barcelona, Valencia, Seville, Zaragoza, Malaga, Murcia, Palma, Bilbao, Alicante, Cordoba, Valladolid, Vigo, Gijon",
  Portugal: "Lisbon, Porto, Braga, Coimbra, Faro, Aveiro, Setubal, Leiria, Funchal, Evora",
  Netherlands: "Amsterdam, Rotterdam, The Hague, Utrecht, Eindhoven, Tilburg, Groningen, Breda, Nijmegen, Haarlem, Arnhem, Zwolle, Maastricht, Leiden, Delft, Alkmaar, Venlo, Dordrecht",
  Belgium: "Brussels, Antwerp, Ghent, Bruges, Leuven, Liege, Namur, Charleroi, Hasselt, Mechelen",
  Switzerland: "Zurich, Geneva, Basel, Lausanne, Bern, Lucerne, Lugano, Winterthur, St. Gallen, Fribourg, Zug, Chur, Neuchatel",
  Austria: "Vienna, Graz, Linz, Salzburg, Innsbruck, Klagenfurt, Villach, Wels, Dornbirn",
  Sweden: "Stockholm, Gothenburg, Malmo, Uppsala, Vasteras, Orebro, Linkoping, Helsingborg, Jonkoping",
  Norway: "Oslo, Bergen, Trondheim, Stavanger, Tromso, Kristiansand, Drammen, Fredrikstad",
  Denmark: "Copenhagen, Aarhus, Odense, Aalborg, Esbjerg, Randers, Horsens",
  Finland: "Helsinki, Espoo, Tampere, Vantaa, Oulu, Turku, Jyvaskyla, Kuopio",
  NewZealand: "Auckland, Wellington, Christchurch, Hamilton, Tauranga, Dunedin, Palmerston North, Napier, Nelson",
  Thailand: "Bangkok, Chiang Mai, Pattaya, Phuket, Hat Yai, Khon Kaen, Udon Thani, Nakhon Ratchasima",
  Indonesia: "Jakarta, Surabaya, Bandung, Medan, Semarang, Makassar, Denpasar, Yogyakarta, Balikpapan",
  Philippines: "Manila, Quezon City, Cebu, Davao, Makati, Pasig, Taguig, Baguio, Iloilo",
  Vietnam: "Ho Chi Minh City, Hanoi, Da Nang, Hai Phong, Can Tho, Hue, Nha Trang",
  Japan: "Tokyo, Osaka, Kyoto, Yokohama, Nagoya, Kobe, Sapporo, Fukuoka, Hiroshima, Sendai",
  SouthKorea: "Seoul, Busan, Incheon, Daegu, Daejeon, Gwangju, Suwon, Ulsan",
  India: "Mumbai, Delhi, Bengaluru, Hyderabad, Chennai, Kolkata, Pune, Ahmedabad, Jaipur, Surat, Lucknow, Kanpur, Indore, Kochi, Coimbatore, Madurai, Nagpur, Visakhapatnam, Vijayawada, Mysuru, Thiruvananthapuram, Chandigarh, Bhopal, Bhubaneswar, Guwahati, Mangalore, Nashik, Noida, Gurugram, Faridabad",
  Bahrain: "Manama, Muharraq, Riffa",
  SouthAfrica: "Johannesburg, Cape Town, Durban, Pretoria, Port Elizabeth, Bloemfontein",
  Brazil: "Sao Paulo, Rio de Janeiro, Brasilia, Salvador, Fortaleza, Belo Horizonte, Curitiba, Recife",
  Mexico: "Mexico City, Guadalajara, Monterrey, Puebla, Tijuana, Cancun",
  Argentina: "Buenos Aires, Cordoba, Rosario, Mendoza",
  Chile: "Santiago, Valparaiso, Concepcion",
  Turkey: "Istanbul, Ankara, Izmir, Bursa, Antalya, Adana",
  Greece: "Athens, Thessaloniki, Patras, Heraklion",
  Poland: "Warsaw, Krakow, Wroclaw, Poznan, Gdansk, Lodz",
  CzechRepublic: "Prague, Brno, Ostrava, Plzen",
  Hungary: "Budapest, Debrecen, Szeged",
  Romania: "Bucharest, Cluj-Napoca, Timisoara, Brasov",
  Russia: "Moscow, Saint Petersburg, Kazan, Novosibirsk, Yekaterinburg"
};

const decodeHtml = (html: string) => {
  if (typeof window === "undefined") return html;
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value.replace(/\u00A0/g, " ").replace(/&nbsp;/g, " ");
};

export default function FooterV2() {
  const { isLoggedIn } = useAuth();
  const { fastPush } = useFastRouter();
  const [categories, setCategories] = useState<MenuNode[]>([]);
  const [activeCityTab, setActiveCityTab] = useState<string>("USA");
  const [settings, setSettings] = useState<GeneralSettingsResponse["data"] | null>(null);
  const [pages, setPages] = useState<PagesListApiResponse["data"]>([]);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const res = await fetchFooterMenu();
        if (isMounted && res?.data) {
          const { nodes } = adaptMenuData(res.data);
          setCategories(nodes?.length > 0 ? nodes : fallbackCategories);
        } else {
          setCategories(fallbackCategories);
        }
      } catch (err) {
        console.error("Failed to load footer categories", err);
        if (isMounted) setCategories(fallbackCategories);
      }
    };

    const loadSettings = async () => {
      try {
        const res = await fetchGeneralSettings();
        if (isMounted && res?.data) {
          setSettings(res.data);
        }
      } catch (err) {
        console.error("Failed to load footer settings", err);
      }
    };

    const loadPages = async () => {
      try {
        const data = await fetchAllPages();
        if (isMounted) {
          setPages(data);
        }
      } catch (err) {
        console.error("Failed to load footer pages", err);
      }
    };

    loadCategories();
    loadSettings();
    loadPages();

    return () => {
      isMounted = false;
    };
  }, []);


  const currentCategories = categories.length > 0 ? categories : fallbackCategories;

  return (
    <footer className="w-full bg-[#f8fafc] border-t border-slate-200 text-slate-700  pb-0 font-sans" role="contentinfo">
      {/* Trustpilot Customer Reviews Section */}
      <TrustpilotFooterSection />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 space-y-10 pt-10">


        <hr className="border-slate-200" />

        {/* Section 2: Core Info Columns (About, Categories, Policies, Account, Cities) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 items-start">
          
          {/* Col 1: About Us */}
          <div className="hidden md:block space-y-4 col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.ABOUT_TITLE}
            </h4>
            <div className="space-y-3 leading-relaxed text-sm text-slate-500 text-justify">
              {settings?.general_settings?.about_us ? (
                <div 
                  className="text-xs leading-relaxed break-words whitespace-normal"
                  style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}
                  dangerouslySetInnerHTML={{ __html: decodeHtml(settings.general_settings.about_us) }}
                />
              ) : (
                <p className="text-xs">
                  {LABELS.BRAND.ABOUT_FALLBACK}
                </p>
              )}
              <p className="text-[10px] font-semibold text-slate-400">
                {LABELS.BRAND.NAME} is a product of {LABELS.BRAND.PRODUCT_OF}.
              </p>
            </div>
          </div>

          {/* Col 2: Categories */}
          <div className="hidden md:block space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.CATEGORIES_TITLE}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              {currentCategories.map((cat) => (

                <li key={cat.id}>
                  <Link href={cat.href} className="hover:text-[var(--brand-accent)] transition-colors text-xs">
                    {cat.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/crazy-deals" className="hover:text-[var(--brand-accent)] transition-colors text-xs">
                  Deals &amp; Offers
                </Link>
              </li>
              <li>
                <Link href="/contact" className="hover:text-[var(--brand-accent)] transition-colors text-xs">
                  Contact Us
                </Link>
              </li>
            </ul>


          </div>

          {/* Col 3: Policies & Terms */}
          <div className="space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.POLICIES_TITLE}
            </h4>
            <ul className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-2.5 text-sm text-slate-500">
              <li>
                <Link
                  href="/shipping-policy"
                  className="hover:text-[var(--brand-accent)] transition-colors text-xs font-medium"
                >
                  Shipping Policy
                </Link>
              </li>
              {pages.map((page) => (
                <li key={page.id}>
                  <Link
                    href={`/pages/${page.slug}`}
                    className="hover:text-[var(--brand-accent)] transition-colors text-xs"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Account */}
          <div className="hidden md:block space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.ACCOUNT_TITLE}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              {isLoggedIn ? (
                <>
                  <li><Link href="/user-profile" className="hover:text-[var(--brand-accent)] transition-colors text-xs">Personal Info</Link></li>
                  <li><Link href="/user-profile?tab=orders" className="hover:text-[var(--brand-accent)] transition-colors text-xs">Orders</Link></li>
                </>
              ) : (
                <>
                  <li>
                    <span
                      onClick={() => fastPush("/sign-in")}
                      className="hover:text-[var(--brand-accent)] transition-colors text-xs cursor-pointer"
                    >
                      Login
                    </span>
                  </li>
                  <li>
                    <span
                      onClick={() => fastPush("/sign-up")}
                      className="hover:text-[var(--brand-accent)] transition-colors text-xs cursor-pointer"
                    >
                      Register
                    </span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Col 5: Major cities serving */}
          <div className="hidden md:block space-y-4 col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.SERVING_LOCATIONS_TITLE}
            </h4>
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] flex flex-col h-[240px]">
              <style dangerouslySetInnerHTML={{ __html: `
                .custom-thin-scrollbar::-webkit-scrollbar {
                  width: 4px;
                  height: 4px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-track {
                  background: #F8FAFC;
                  border-radius: 2px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-thumb {
                  background: #CBD5E1;
                  border-radius: 2px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #94A3B8;
                }
              `}} />
              
              <div className="flex gap-2 flex-1 min-h-0">
                {/* Tabs side-list */}
                <div className="w-[85px] border-r border-slate-100 flex flex-col gap-1 pr-1 overflow-y-auto custom-thin-scrollbar text-left">
                  {Object.keys(citiesData).map((tab) => {
                    const isSelected = activeCityTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveCityTab(tab)}
                        className={`text-left text-[11px] font-semibold px-2 py-1 rounded transition-all cursor-pointer truncate shrink-0 ${
                          isSelected
                            ? "bg-[var(--brand-accent)] text-white shadow-sm font-bold"
                            : "text-slate-500 hover:bg-slate-50 hover:text-slate-850"
                        }`}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
                
                {/* Cities scrollable list */}
                <div className="flex-1 overflow-y-auto custom-thin-scrollbar text-[10px] text-slate-400 leading-relaxed pr-1 text-justify scroll-smooth">
                  {citiesData[activeCityTab]}
                </div>
              </div>
            </div>
          </div>

        </div>

        <hr className="border-slate-200" />

        {/* Section 3: Bottom info area (Contact Information block & Social Media Icons side-by-side) */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-6">
          {/* Contact Information Text block */}
          <div className="space-y-3 max-w-xl">
            <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">{LABELS.FOOTER.CONTACT_US_TITLE}</h4>
            {settings?.contact?.address ? (
              <p className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">
                <span className="font-semibold text-slate-900">{LABELS.BRAND.FULL_NAME}</span> — {settings.contact.address}, {[settings.contact.city, settings.contact.state, settings.contact.zip_code].filter(Boolean).join(", ")}, {settings.contact.country}
              </p>
            ) : (
              <p className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">
                {/* <span className="font-semibold text-slate-900">{LABELS.BRAND.FULL_NAME}</span> — {LABELS.BRAND.DEFAULT_ADDRESS}, {LABELS.BRAND.DEFAULT_CITY}, {LABELS.BRAND.DEFAULT_STATE} {LABELS.BRAND.DEFAULT_ZIP}, {LABELS.BRAND.DEFAULT_COUNTRY} */}
              </p>
            )}
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs md:text-sm font-semibold text-slate-800 mt-2">
              <span className="flex items-center gap-2">
                {/* WhatsApp/Phone Greenish Icon */}
                <svg className="w-4 h-4 text-emerald-600 fill-current shrink-0" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.458L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.42 9.864-9.852.002-2.63-1.023-5.101-2.885-6.967C16.528 1.967 14.063.943 11.998.943c-5.441 0-9.87 4.42-9.874 9.855-.001 1.761.472 3.483 1.368 5.031L2.482 21.52l5.165-1.366zM17.15 14.49c-.28-.14-1.65-.81-1.91-.9-.26-.1-.45-.14-.64.14-.19.28-.73.9-.9 1.1-.17.19-.34.21-.62.07-2.18-1.09-3.41-2.48-4.14-3.74-.19-.33-.02-.51.15-.68.15-.15.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.02-.55-.08-.14-.64-1.55-.88-2.11-.23-.57-.47-.49-.64-.5-.17-.01-.36-.01-.55-.01-.19 0-.5.07-.76.36-.26.28-1 .98-1 2.4s1.03 2.78 1.18 2.97c.15.19 2.03 3.1 4.92 4.35.69.29 1.23.47 1.65.6.69.22 1.32.19 1.82.11.56-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.26-.19-.54-.33z"/>
                </svg>
                Phone: <a href={`tel:${settings?.contact?.secondary_phone || settings?.contact?.phone }`} className="hover:text-[var(--brand-accent)] transition-colors">{settings?.contact?.secondary_phone || settings?.contact?.phone }</a>
              </span>
              <span className="flex items-center gap-2">
                {/* Envelope/Email Icon */}
                <svg className="w-4 h-4 text-slate-800 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                Email: <a href={`mailto:${settings?.contact?.email }`} className="hover:text-[var(--brand-accent)] transition-colors">{settings?.contact?.email}</a>
              </span>
            </div>
          </div>

          {/* Social Icons Connect Block */}
          <div className="space-y-2 shrink-0">
            <h4 className="font-semibold text-slate-950 text-xs text-left md:text-right">{LABELS.FOOTER.CONNECT_WITH_US_TITLE}</h4>
            <div className="flex gap-2">
              <a href={LABELS.FACEBOOK_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Facebook">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M9 8H7v3h2v9h4v-9h3.6l.4-3H13V6c0-.5.5-1 1-1h2V1h-3c-3 0-5 2-5 5v2z"/></svg>
              </a>
              <a href={LABELS.INSTAGRAM_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Instagram">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href={LABELS.TWITTER_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Twitter">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              </a>
              <a href={LABELS.YOUTUBE_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="YouTube">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.163a3.003 3.003 0 00-2.11-2.11C19.518 3.545 12 3.545 12 3.545s-7.518 0-9.388.507a3.003 3.003 0 00-2.11 2.11C0 8.033 0 12 0 12s0 3.967.502 5.837a3.003 3.003 0 002.11 2.11c1.87.507 9.388.507 9.388.507s7.518 0 9.388-.507a3.003 3.003 0 002.11-2.11C24 15.967 24 12 24 12s0-3.967-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href={LABELS.LINKEDIN_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="LinkedIn">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href={LABELS.PINTEREST_LINK} className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Pinterest">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.168 1.777 2.168 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.166-1.495-.694-2.433-2.878-2.433-4.629 0-3.764 2.728-7.229 7.889-7.229 4.144 0 7.365 2.953 7.365 6.9 0 4.116-2.595 7.431-6.199 7.431-1.21 0-2.348-.63-2.738-1.373 0 0-.599 2.282-.746 2.833-.27.103-.997 1.248-1.5 2.081 1.075.316 2.207.487 3.385.487 6.622 0 11.988-5.366 11.988-11.985C23.985 5.367 18.618 0 12.017 0z"/></svg>
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Bar: Links and Copyright */}
      <div className="w-full bg-[#f1f5f9] border-t border-slate-200 pt-6 pb-[72px] md:py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-slate-500 gap-4">
       
          <div className="text-13 text-slate-400 font-medium">
            © {new Date().getFullYear()} {LABELS.FOOTER.DOMAIN}. {LABELS.FOOTER.COPYRIGHT_PREFIX}
          </div>
        </div>
      </div>
    </footer>
  );
}
